import { debounce, reject, throttle } from "lodash";
import React, {
  useEffect,
  useState,
  createContext,
  useRef,
  useMemo,
} from "react";
import { io } from "socket.io-client";
import { convertStringToNumber, extractIds, generateToast } from "../helper";
import { INITIAL_STATE } from "../constant/Editor";
import { useIdb } from "../utils/IndexedDB";
import { CalculateCosts, getTPEResp } from "../helper/CostHelpers";
import {
  shouldAutoPopulateSDClause,
  contractContainsSDClause,
  injectSDClause,
  removeSDClauseIfUnmodified,
} from "../utils/sdTransportationClause";
import { ERROR, SUCCESS, TOP_RIGHT } from "../constant";
import { createClient, createClientType } from "../services/ClientService";
import { createRegion } from "../services/RegionService";
import { createEventType, getEventTypes } from "../services/EventService";
import { createSchool } from "../services/SchoolService";
import {
  getContracts,
  getContractsTemplate,
  getContractsTemplateList,
  getParentContractTitles,
  updateContract,
  updateContractsTemplate,
} from "../services/ContractServices";
import { getRegions } from "../services/RegionService";
import { getClientTypes } from "../services/ClientTypeServices";
import { getClients } from "../services/ClientService";
import { getSchools } from "../services/SchoolService";
import { getAllEmployee, getSalesEmployee } from "../services/EmployeeService";
import { getStaffDetails } from "../services/StaffServices";
import {
  getTransportationDetails,
  getTransportationType,
} from "../services/TransportationServices";
import { getProductionDetails } from "../services/ProductionServcies";
import { getVenueData } from "../services/VenueServices";
import { addSocketIdInterceptor, instance } from "../services/ServicesApi";
import { useDispatch } from "react-redux";
import { getEventOptionsThunk } from "../redux/reducers/EventReducer";
// The backend refuses a socket handshake without a valid access token (project-management#504), so the
// socket waits for one (autoConnect: false) and sends the current token on every (re)connect.
let socketToken = null;
export const socket = io(process.env.REACT_APP_API_HOST.replace("/api", ""), {
  withCredentials: true,
  autoConnect: false,
  auth: (cb) => {
    socketToken = localStorage.getItem("token");
    cb({ token: socketToken });
  },
});

// Connects once a token exists, and reconnects when the token belongs to a different sign-in, so the
// user the server binds to the socket is always the one now signed in.
export const ensureSocketConnected = () => {
  const token = localStorage.getItem("token");
  if (!token) return;
  if ((socket.connected || socket.active) && socketToken === token) return;
  if (socket.connected || socket.active) socket.disconnect();
  socket.connect();
};

// A handshake the server refuses (expired token after a laptop sleeps, or a tab opened before this build)
// is not retried by socket.io-client. Retry with backoff, and right away once the REST layer has refreshed
// the token (ServicesApi dispatches "tpe:token-refreshed"), so presence and chat come back without a reload.
let socketRetryMs = 15000;
let socketRetryTimer = null;
const reconnectSocketIfDown = () => {
  clearTimeout(socketRetryTimer);
  socketRetryTimer = null;
  if (!socket.connected && !socket.active && localStorage.getItem("token")) socket.connect();
};
socket.on("connect", () => {
  socketRetryMs = 15000;
  clearTimeout(socketRetryTimer);
  socketRetryTimer = null;
});
socket.on("connect_error", () => {
  if (socket.active || socketRetryTimer) return;
  socketRetryTimer = setTimeout(reconnectSocketIfDown, socketRetryMs);
  socketRetryMs = Math.min(socketRetryMs * 2, 300000);
});
window.addEventListener("tpe:token-refreshed", reconnectSocketIfDown);

// Module-scoped (not component state): needs to be importable from the other
// login_user call sites outside AppProvider's subtree (LoginForm.js,
// Routes.js), and must survive component remounts. Initialized to "now" at
// module load, so a fresh page load reports ~0ms since activity (idle-timeout
// #264 AC: "a refresh restores active"). Deliberately NOT reset by socket
// connect/reconnect - only by a real DOM interaction - so a reconnect alone
// can never count as activity (#264 AC: "connected but idle stays idle").
let lastActivityAt = Date.now();
export const getMsSinceLastActivity = () => Date.now() - lastActivityAt;
export const pingActivity = throttle(
  () => {
    // A backgrounded tab is not someone at their desk - stop reporting
    // activity while hidden rather than let the interval events that still
    // fire in some browsers count as presence (#264).
    if (document.visibilityState !== "visible") return;
    lastActivityAt = Date.now();
    // volatile: dropped while offline instead of queuing and flushing past the server event limit (#504).
    socket.volatile.emit("activity_ping");
  },
  60000,
  { leading: true, trailing: false }
);

export const AppContext = createContext();

const loadingType = {
  type: "",
  isLoading: false,
};

const AppProvider = ({ children }) => {
  let initialState = {};
  const dispatch = useDispatch();
  const [isLoading, setLoading] = useState(true);
  const [editorState, setEditorState] = useState();
  const [activeUsers, setActiveUsers] = useState([]);
  const [activeUsersSummary, setActiveUsersSummary] = useState([]);
  // Idle-timeout threshold (#264), pushed from the backend on every
  // user_update so the panel never has to duplicate PRESENCE_IDLE_TIMEOUT_MINUTES.
  // Defaults to the backend's own default until the first payload arrives.
  const [idleTimeoutMinutes, setIdleTimeoutMinutes] = useState(15);
  const [templateText, setTemplateText] = useState();
  const [templateVersion, setTemplateVersion] = useState();
  const [isTemplateLoading, setIsTemplateLoading] = useState(true);
  // All available contract templates (#284, template picker) — separate from
  // templateText/templateVersion above, which stay specifically "the Default
  // template" (used as the creation-time fallback).
  const [templates, setTemplates] = useState([]);
  const [activeContracts, setActiveContracts] = useState([]);
  const summaryRef = useRef();
  const freezeHandle = useRef(false);
  const mapIndexRef = useRef(0);
  const formTabIndex = useRef(0);
  const activeUsersRef = useRef([]);
  const [summary, setSummary] = useState([{}]);
  const [contractTab, setContractTabs] = useState([]);
  const [mapIndex, setMapIndex] = useState(undefined);

  // Stats tab state persistence (prevents reset on navigation)
  const [statsFilterState, setStatsFilterState] = useState(null);
  const [statsTypeFilters, setStatsTypeFilters] = useState(null);
  const [statsPredefinedDateSelections, setStatsPredefinedDateSelections] = useState(null);
  // Tracks predefined labels for combined AND/OR mode: { event: [label, label], sign: [label, label] }
  const [statsCombinedPredefinedLabels, setStatsCombinedPredefinedLabels] = useState(null);
  const [editIndex, setSetIndex] = useState(undefined);
  const [user, setUser] = useState();
  const [salesEmp, setSalesEmp] = useState([]);
  const [isTypeLoading, setTypeLoading] = useState(loadingType);
  const [contracts, setContracts] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [parentOptions, setParentOptions] = useState([]);

  const { role } = user || {};
  const isStaffer = ["Staffer"].includes(role);
  const rolesToAccess = ["Owner"];
  const hasUpdatePower = rolesToAccess.includes(role);

  const parentTitleWithOptions = useMemo(() => {
    return parentOptions?.map(({ id, title }) => ({
      id,
      name: title,
    }));
  }, [parentOptions]);

  const getSummary = (index = 0) => {
    return summary[index];
  };

  const [notifications, setNotifications] = useState([]);
  const [transportationPriceList, setTransportationPriceList] = useState([]);
  const { totalESCost, totalPCost, totalTransCost, totalAccountCost } =
    CalculateCosts(getSummary(mapIndex));
  const contractTotal = totalESCost + totalTransCost + totalPCost;
  const setTabRef = useRef();
  const tabIndex = useRef();
  const initialFetched = useRef();
  const isApiLoading = useRef(false);
  const editorInstanceRef = useRef(null);

  const setEditorInstance = (editor) => {
    editorInstanceRef.current = editor;
  };
  const [editorDBState, setEditorDB, isStateLoading] = useIdb(
    "editor-state",
    INITIAL_STATE
  );
  const isAdmin = [
    "admin",
    "event director lead auditor",
    "event director lead",
    "owner",
  ].includes(role?.toLowerCase());

  const fetchDetails = async () => {
    // Only fetch details if user is authenticated and token exists
    const token = localStorage.getItem("token");
    if (!token || !isAuthenticated) {
      console.log("⚠️ Skipping fetchDetails - no token or not authenticated");
      return;
    }

    setTypeLoading({
      type: "Contracts",
      isLoading: true,
    });

    getContracts({ shouldLoadEvents: true, shouldLoadClients: true })
      .then((contract) => {
        setContracts(contract.data);
      })
      .finally(() => setTypeLoading(loadingType));
  };

  const fetchParentOptions = async () => {
    const token = localStorage.getItem("token");
    if (!token || !isAuthenticated) {
      console.log("⚠️ Skipping fetchParentOptions - no token or not authenticated");
      return;
    }
    getParentContractTitles().then(({ data }) => {
      setParentOptions(data);
    });
  };

  const handleContractUpdate = useMemo(() => {
    return debounce(async (id, obj) => {
      try {
        await updateContract(
          id,
          extractIds(obj, {
            addClientId: true,
          })
        );
      } catch (err) {
        console.log({ err });
        generateToast(
          "Error in saving updates, Please refresh site & try again!",
          ERROR,
          TOP_RIGHT
        );
      }
    }, 500);
  }, []);

  const updatePDFContract = async (id, pdfContract) => {
    handleContractUpdate(id, { pdfContract });
  };

  const injectSDTransportationClause = (regionName, eventClients, eventId) => {
    if (!shouldAutoPopulateSDClause(regionName, eventClients)) return;

    const editor = editorInstanceRef.current;
    const html = editor ? editor.getData() : summaryRef.current?.[mapIndexRef.current]?.pdfContract;
    if (!html || contractContainsSDClause(html)) return;

    const updated = injectSDClause(html);
    if (updated === html) return;

    if (editor) editor.setData(updated);
    if (!editor && mapIndexRef.current !== undefined) {
      const arr = [...summaryRef.current];
      arr[mapIndexRef.current] = { ...arr[mapIndexRef.current], pdfContract: updated };
      summaryRef.current = arr;
      setSummary(arr);
    }
    if (eventId) updatePDFContract(eventId, updated);
  };

  const removeSDTransportationClause = (regionName, eventClients, eventId) => {
    if (shouldAutoPopulateSDClause(regionName, eventClients)) return;

    const editor = editorInstanceRef.current;
    const html = editor ? editor.getData() : summaryRef.current?.[mapIndexRef.current]?.pdfContract;
    if (!html || !contractContainsSDClause(html)) return;

    const updated = removeSDClauseIfUnmodified(html);
    if (updated === html) return;

    if (editor) editor.setData(updated);
    if (!editor && mapIndexRef.current !== undefined) {
      const arr = [...summaryRef.current];
      arr[mapIndexRef.current] = { ...arr[mapIndexRef.current], pdfContract: updated };
      summaryRef.current = arr;
      setSummary(arr);
    }
    if (eventId) updatePDFContract(eventId, updated);
  };

  useEffect(() => {
    user && isAuthenticated && fetchDetails();

    // eslint-disable-next-line
  }, [user, isAuthenticated]);

  const handleUpdate = (data) => {
    const { event_sender, contractData } = data;
    if (event_sender !== socket.id) {
      let summaryArray = [...summaryRef.current];

      const indexOf = summaryArray.findIndex(({ id }) => {
        return id === contractData.id;
      });
      if (indexOf >= 1) {
        freezeHandle.current = true;
        summaryArray[indexOf] = { ...summaryArray[indexOf], ...contractData };
        summaryRef.current = summaryArray;
        setSummary(summaryArray);
      }
    }
  };

  useEffect(() => {
    if (socket) {
      socket.on("user_update", (data) => {
        const usersMap = data?.users || {};
        const summary = Array.isArray(data?.summary) ? data.summary : [];
        const liveUsers = Object.values(usersMap).map((activeUser) => ({
          ...activeUser.userDetails,
          active_input: activeUser.active_input,
        }));
        setActiveUsers(liveUsers);
        activeUsersRef.current = liveUsers;
        setActiveUsersSummary(summary);
        if (typeof data?.idleTimeoutMinutes === "number") {
          setIdleTimeoutMinutes(data.idleTimeoutMinutes);
        }
      });

      socket.on("channel_update", (data) => {
        handleUpdate(data);
      });

      socket.on("contract_update", (data) => {
        setActiveContracts(data);
      });
    }
    // eslint-disable-next-line
  }, [socket]);

  // Re-register the socket session on every (re)connect and on the server's
  // post-restart resync broadcast, so a network blip or backend deploy
  // doesn't leave the user silently unregistered until a full page reload.
  useEffect(() => {
    const registerSession = () => {
      if (user?.id) {
        socket.emit("login_user", {
          ...user,
          socketId: socket.id,
          msSinceLastActivity: getMsSinceLastActivity(),
        });
      }
    };
    socket.on("connect", registerSession);
    socket.on("resync_presence", registerSession);
    // One login_user per connect (#504): if this sign-in's socket is already up, register now;
    // otherwise connect, and the "connect" handler registers. The sign-in screens no longer emit it.
    if (user?.id) {
      if (socket.connected && socketToken === localStorage.getItem("token")) registerSession();
      else ensureSocketConnected();
    }
    return () => {
      socket.off("connect", registerSession);
      socket.off("resync_presence", registerSession);
    };
  }, [user]);

  // Throttled real-interaction heartbeat driving the idle-timeout display
  // (#264). Separate from handlePointMove below, which is debounced (fires
  // only after 500ms of mouse *stillness*) and gated to a specific contract
  // tab - reusing it as-is would miss idle detection during continuous
  // mouse movement and on any non-contract view. Capture phase so a
  // descendant's stopPropagation() (used elsewhere in this app) can't
  // swallow the signal.
  useEffect(() => {
    const events = ["mousemove", "keydown", "click", "scroll"];
    events.forEach((event) =>
      document.addEventListener(event, pingActivity, true)
    );
    return () => {
      events.forEach((event) =>
        document.removeEventListener(event, pingActivity, true)
      );
    };
  }, []);

  const handleContractDetails = (data, index) => {
    const inputArray = [...contractTab];
    inputArray[index - 1] = data;
    setContractTabs(inputArray);
  };

  const handleSummary = async (obj, index = 0) => {
    if (!index) return {};
    handleContractDetails(obj, index);
    const data = [...summary];
    data[index] = obj;
    setSummary(data);
    summaryRef.current = data;
    localStorage.setItem("data", JSON.stringify(data));
  };

  const closeFun = (index, e, isCreate, delId) => {
    e?.stopPropagation();
    const inputArray = [...contractTab];
    let summaryArray = [...summary];
    if (isCreate) {
      summaryArray[0] = initialState;
    } else {
      summaryArray = reject(summaryArray, function (el) {
        return el?.id === delId;
      });
    }
    inputArray.splice(index, 1);
    setSummary(summaryArray);
    summaryRef.current = summaryArray;
    setContractTabs(inputArray);

    // Contract tabs start at index 1 (index 0 is the "All Contracts" tab)
    const closedTabIndex = index + 1;
    const currentTab = tabIndex?.current;

    if (currentTab === closedTabIndex) {
      // We closed the currently active tab - navigate to another one
      if (inputArray.length === 0) {
        // No more contract tabs, go to "All Contracts" (index 0)
        setTabRef.current(0);
      } else if (index === 0) {
        // Closed first contract tab, stay at index 1 (which is now the next contract)
        setTabRef.current(1);
      } else {
        // Go to previous tab
        setTabRef.current(closedTabIndex - 1);
      }
    } else if (closedTabIndex < currentTab) {
      // Closed a tab before the current one - adjust index to stay on same content
      setTabRef.current(currentTab - 1);
    }
    // If closed tab is after current tab, no adjustment needed
  };

  const handleReset = () => {
    setContractTabs([]);
    setSummary([]);
    summaryRef.current = [];
    setUser();
    setIsAuthenticated(0);
  };

  const getTransQuant = () => {
    const summary = getSummary(mapIndex);
    const { transportationBillings } = summary;
    const transQuant = transportationBillings.map((data) => data?.quantity);

    const totalTransQuant = transQuant?.reduce(
      (a, b) => convertStringToNumber(a || 0) + convertStringToNumber(b || 0),
      0
    );
    return totalTransQuant;
  };

  const fetchContractTemplateToState = async () => {
    setIsTemplateLoading(true);

    try {
      const res = await getContractsTemplate();
      let templateHtml = INITIAL_STATE;

      if (!res.data) {
        const createRes = await updateContractsTemplate({ html: templateHtml });
        if (createRes.success) {
          const res = await getContractsTemplate();
          templateHtml = res?.data?.html;
        }
      } else {
        templateHtml = res?.data?.html;
      }

      setTemplateVersion(res?.data?.versionId);
      setTemplateText(templateHtml);
    } catch (err) {
      console.log("Error:", err);
    } finally {
      setIsTemplateLoading(false);
    }
  };

  // Populates the template picker's options (#284). Separate from
  // fetchContractTemplateToState above, which only ever loads the one
  // Default template.
  const fetchContractTemplatesList = async () => {
    try {
      const res = await getContractsTemplateList();
      setTemplates(res?.data || []);
    } catch (err) {
      console.log("Error fetching contract templates list:", err);
    }
  };

  const getUpFrontCost = (propsSummary) => {
    const summary = propsSummary || getSummary(mapIndex);
    const {
      expenses,
      finalBarSales,
      barGuarantee,
      clientResponsibility,
      shouldConsiderClientResponsibility,
    } = summary || {};

    const processExpenses = () => {
      const beverage = expenses
        ?.filter((obj) => obj.expenseType?.name === "Beverage")
        .sort((a, b) => a.id - b.id);

      const food = expenses
        ?.filter((obj) => obj.expenseType?.name === "Food")
        .sort((a, b) => a.id - b.id);

      const rental = expenses
        ?.filter((obj) => obj.expenseType?.name === "Misc")
        .sort((a, b) => a.id - b.id);

      return { beverage, food, rental };
    };

    const { beverage, rental, food } = processExpenses();

    const sumRentalCost = rental?.map(({ cost }) => cost);
    const sumFoodCost = food?.map(({ cost }) => cost);
    const sumBevCost = beverage?.map(({ cost }) => cost);

    const rental_cost = sumRentalCost?.reduce(
      (a, b) => convertStringToNumber(a || 0) + convertStringToNumber(b || 0),
      0
    );

    const food_cost = sumFoodCost?.reduce(
      (a, b) => convertStringToNumber(a || 0) + convertStringToNumber(b || 0),
      0
    );

    const bev_cost = sumBevCost?.reduce(
      (a, b) => convertStringToNumber(a || 0) + convertStringToNumber(b || 0),
      0
    );

    function TotalBevCost() {
      const isGambleSucceed =
        convertStringToNumber(finalBarSales) >
        convertStringToNumber(barGuarantee);
      if (!convertStringToNumber(finalBarSales)) {
        return getTPEResp(summary) + bev_cost;
      } else if (isGambleSucceed) {
        return 0 + bev_cost;
      }
      const clientRespDiff =
        clientResponsibility - finalBarSales >= 0
          ? clientResponsibility - finalBarSales
          : 0;

      const clientDiff = shouldConsiderClientResponsibility
        ? clientRespDiff
        : 0;
      return (
        convertStringToNumber(bev_cost) +
        convertStringToNumber(barGuarantee - finalBarSales - clientDiff)
      );
    }

    const bevTotalCost = TotalBevCost();

    return rental_cost + food_cost + bevTotalCost || 0;
  };

  const [getClientName, setGetClientName] = useState([]);
  const [barSalesClients, setBarSalesClients] = useState([]);
  const [getRegion, setGetRegion] = useState([]);
  const [getClientType, setGetClientType] = useState([]);
  const [getSchoolName, setGetSchoolName] = useState([]);
  const [getEventType, setGetEventType] = useState([]);
  const [getEmployees, setEmployees] = useState([]);
  const [getProductionPriceList, setProductionPriceList] = useState([]);
  const [getTransPriceList, setTransPriceList] = useState([]);
  const [getTransType, setTransType] = useState([]);
  const [getVenue, setGetVenue] = useState([]);
  const [getESPriceList, setESPriceList] = useState([]);

  // Memoize the mapped salesEmp data to prevent continuous remapping
  const salesEmpWithOptions = useMemo(() => {
    return salesEmp?.map((emp) => ({
      id: emp?.id,
      name: `${emp?.firstName} ${emp?.lastName}`, // Combine first name and last name
    }));
  }, [salesEmp]);

  const fetchClient = () => {
    const token = localStorage.getItem("token");
    if (!token || !isAuthenticated) {
      console.log("⚠️ Skipping fetchClient - no token or not authenticated");
      return;
    }
    getClients().then((getClientName) => setGetClientName(getClientName.data));
  };

  const fetchBarSalesClients = async () => {
    return getClients({ havingBarSales: true }).then((getClientName) => {
      setBarSalesClients(getClientName.data);
      return getClientName.data;
    });
  };

  const onGetESPricing = async () => {
    getStaffDetails().then((res) => setESPriceList(res.data));
  };

  const getSalesEmployees = () => {
    getSalesEmployee().then((res) => {
      setSalesEmp(res);
    });
  };

  const getAllEmployees = () => {
    getAllEmployee().then((res) => {
      setAllEmployees(res);
    });
  };

  const initializeEditor = async (mockState) => {
    const editorData = mockState;
    const contentState = editorData;
    setEditorState(contentState);
  };

  useEffect(() => {
    initializeEditor();
    // eslint-disable-next-line
  }, [isStateLoading]);

  const getNotifications = () => {
    // Legacy call — `/v1/user/notifications` does not exist on the backend
    // (notifications live under /v1/notifications via NotificationServices),
    // and this relative fetch resolves against the frontend host, so it gets
    // index.html back and the .json() parse rejects UNHANDLED on every page
    // load. Keep the (no-op) behavior but swallow the rejection.
    fetch(`/v1/user/notifications`, {
      method: "GET",
      headers: { jwt_token: localStorage.token },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data?.msg) return;
        return setNotifications(data);
      })
      .catch(() => {});
  };
  // When the user moves their pointer, update their presence
  // eslint-disable-next-line
  const handlePointMove = React.useCallback(
    debounce(
      (e) => {
        const summary = summaryRef.current;

        const filterContractActiveUser = activeUsersRef?.current?.filter(
          ({ active_input }) =>
            active_input?.id === summary[mapIndexRef.current]?.id
        );
        const allowedUpdate =
          filterContractActiveUser?.length > 1 ||
          filterContractActiveUser?.[0]?.username !== user.username;

        const focusedId = summary[mapIndexRef.current]?.id;
        // #369: only broadcast focus for a resolved field. Emitting with
        // id: undefined failed backend payload validation ("Invalid payload for
        // FOCUS_INPUT"), which surfaced as app errors alongside the fetch storm.
        if (mapIndexRef.current && allowedUpdate && focusedId != null) {
          const active_input = {
            id: focusedId,
            point: [e.x, e.y],
            user: user.username,
            tabIndex: formTabIndex.current,
          };
          socket.volatile.emit("focus_input", active_input);
          if (!filterContractActiveUser?.length) {
            activeUsersRef.current = [
              ...activeUsersRef.current,
              { active_input, ...(user.employee || {}) },
            ];
          }
        }
      },
      500 // Debounce delay in milliseconds
    ),
    [user]
  );

  useEffect(() => {
    addSocketIdInterceptor(socket);

    // Cleanup the interceptor when component unmounts
    return () => {
      instance.interceptors.request.eject();
    };
    // eslint-disable-next-line
  }, [socket]);

  useEffect(() => {
    if (user && isAuthenticated) {
      // Fetch the notification count

      if (!initialFetched.current) {
        summaryRef.current = [];
        initialFetched.current = true;
        fetchClient();
        fetchParentOptions();
        getAllEmployees();
        getSalesEmployees();
        fetchRegion();
        fetchClientType();
        fetchSchool();
        fetchEventType();
        fetchEmployees();
        onGetProductionPricing();
        onGetTransPricing();
        fetchVenue();
        onGetESPricing();
        onGetTransType();
        dispatch(getEventOptionsThunk());
        document.addEventListener("mousemove", handlePointMove);
      }
    }

    return () => {
      document.removeEventListener("mousemove", handlePointMove);
    };
    // eslint-disable-next-line
  }, [user, isAuthenticated]);

  const fetchRegion = () => {
    getRegions().then((regions) => {
      setGetRegion(regions.data);
    });
  };

  const fetchClientType = () => {
    getClientTypes().then((getClientType) =>
      setGetClientType(getClientType.data)
    );
  };

  const fetchSchool = () => {
    getSchools().then((getSchoolName) => setGetSchoolName(getSchoolName.data));
  };

  const fetchEventType = () => {
    getEventTypes().then((getEventType) => setGetEventType(getEventType.data));
  };

  const fetchEmployees = () => {
    // Legacy call — resolves against the frontend host (no API base URL), so
    // it has never populated `employees`; the .json() parse of the index.html
    // fallback rejected UNHANDLED on every page load. Keep the (no-op)
    // behavior but swallow the rejection.
    fetch("/v1/employees")
      .then((response) => response.json())
      .then((getEmployees) => setEmployees(getEmployees))
      .catch(() => {});
  };

  const onGetProductionPricing = async () => {
    try {
      getProductionDetails().then((res) => setProductionPriceList(res.data));
    } catch (err) {
      console.error(err.message);
    }
  };

  const onGetTransPricing = async () => {
    try {
      getTransportationDetails().then((res) => setTransPriceList(res.data));
    } catch (err) {
      console.error(err.message);
    }
  };

  const onGetTransType = async () => {
    try {
      getTransportationType().then((res) => setTransType(res.data));
    } catch (err) {
      console.error(err.message);
    }
  };

  const fetchVenue = () => {
    getVenueData().then((getVenue) => setGetVenue(getVenue.data));
  };

  const handleClearStorage = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("region");
    localStorage.removeItem("data ");
    localStorage.removeItem("firstname");
    localStorage.removeItem("lastname");
    localStorage.removeItem("totalCapacity");
    localStorage.removeItem("event_staff_tc");
    localStorage.removeItem("transportation_tc");
    localStorage.removeItem("production_tc");
  };

  // Create NEW Client
  const updateClientName = async (name, clientType) => {
    const clientPromise = createClient({
      name,
      clientType,
    });
    return clientPromise
      .then((res) => {
        generateToast("Client Created!", SUCCESS);
        fetchClient();
        return res.data;
      })
      .catch((err) => {
        console.error(err.message);
      });
  };
  // Create NEW Client

  // Create NEW Region
  const updateRegion = async (name) => {
    const regionPromise = createRegion({ name });

    return regionPromise
      .then((res) => {
        generateToast("Region Created!", SUCCESS);
        fetchRegion();
        return res.data;
      })
      .catch((err) => {
        console.error(err.message);
      });
  };

  // Create NEW Event Type
  const updateEventType = async (name) => {
    const eventTypePromise = createEventType({
      name,
    });

    return eventTypePromise
      .then((res) => {
        if (res.data.isApproved === false) {
          generateToast(
            "Event Type submitted for approval. You'll receive a notification once it's accepted or rejected.",
            SUCCESS
          );
        } else {
          generateToast("Event Type Created!", SUCCESS);
        }

        fetchEventType(); // Refresh the list of event types
        return res.data;
      })
      .catch((err) => {
        console.error(err.message);
        generateToast(
          "Failed to create event type. Please try again later.",
          ERROR
        );
      });
  };

  // Create NEW School
  const updateSchool = async (name) => {
    const schoolPromise = createSchool({
      name,
    });

    return schoolPromise
      .then((res) => {
        console.log(
          { res },
          res.data.isApproved,
          res.data.isApproved === false
        );

        if (res.data.isApproved === false) {
          generateToast(
            "School submitted for approval. You'll receive a notification once it's accepted or rejected.",
            SUCCESS
          );
        } else {
          generateToast("School Created!", SUCCESS);
        }

        fetchSchool(); // Refresh the list of schools
        return res.data;
      })
      .catch((err) => {
        console.error(err.message);
        generateToast(
          "Failed to create school. Please try again later.",
          ERROR
        );
      });
  };

  // Create NEW Client Type
  const updateClientType = async (name) => {
    const clientTypePromise = createClientType({
      name,
    });

    return clientTypePromise
      .then((res) => {
        
        if (res.data.isApproved === false) {
          generateToast(
            "Client Type submitted for approval. You'll receive a notification once it's accepted or rejected.",
            SUCCESS
          );
        } else {
          generateToast("Client Type Created!", SUCCESS);
        }

        fetchClientType(); // Refresh the list of client types
        return res.data;
      })
      .catch((err) => {
        console.error(err.message);
        generateToast(
          "Failed to create client type. Please try again later.",
          ERROR
        );
      });
  };

  return (
    <AppContext.Provider
      value={{
        parentTitleWithOptions,
        activeContracts,
        setIsTemplateLoading,
        parentOptions,
        activeUsers,
        activeUsersSummary,
        idleTimeoutMinutes,
        allEmployees,
        closeFun,
        templateText,
        isTemplateLoading,
        templates,
        fetchContractTemplatesList,
        contracts,
        contractTab,
        contractTotal,
        editIndex,
        editorState,
        fetchClient,
        fetchClientType,
        fetchDetails,
        fetchEventType,
        fetchPricing: onGetTransPricing,
        fetchRegion,
        fetchSchool,
        fetchVenue,
        formTabIndex,
        freezeHandle,
        getAllEmployees,
        getClientName,
        getClientType,
        getEmployees,
        getESPriceList,
        getEventType,
        getNotifications,
        getProductionPriceList,
        getRegion,
        getSchoolName,
        getSummary,
        getTransPriceList,
        getTransQuant,
        getUpFrontCost,
        getVenue,
        handleClearStorage,
        handlePointMove,
        handleReset,
        initialFetched,
        initializeEditor,
        isApiLoading,
        isAuthenticated,
        isLoading,
        isTypeLoading,
        mapIndex,
        mapIndexRef,
        notifications,
        onGetESPricing,
        onGetProductionPricing,
        onGetTransPricing,
        salesEmp,
        salesEmpWithOptions,
        setContracts,
        setContractTabs,
        setEditorDB,
        setEditorState,
        setIsAuthenticated,
        setLoading,
        setMapIndex,
        setSetIndex,
        setSummary: handleSummary,
        setSummaryArray: setSummary,
        setTabRef,
        fetchParentOptions,
        fetchContractTemplateToState,
        setTransportationPriceList,
        setTypeLoading,
        setUser,
        socket,
        summary,
        summaryRef,
        tabIndex,
        totalESCost,
        totalPCost,
        totalTransCost,
        transportationPriceList,
        updateClientName,
        updateContract,
        updatePDFContract,
        setEditorInstance,
        injectSDTransportationClause,
        removeSDTransportationClause,
        updateRegion,
        updateEventType,
        updateSchool,
        updateClientType,
        onGetTransType,
        role,
        getTransType,
        user,
        isStaffer,
        isAdmin,
        totalAccountCost,
        templateVersion,
        handleContractUpdate,
        fetchBarSalesClients,
        barSalesClients,
        // Stats tab state persistence
        statsFilterState,
        setStatsFilterState,
        statsTypeFilters,
        setStatsTypeFilters,
        statsPredefinedDateSelections,
        setStatsPredefinedDateSelections,
        statsCombinedPredefinedLabels,
        setStatsCombinedPredefinedLabels,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export default AppProvider;
